import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";

/**
 * TrialResumer
 *
 * Mounted globally inside <AppRoutes/>. Watches for the user transitioning
 * from logged-out → logged-in. If a `pending_trial_start` flag is set in
 * localStorage (placed there when a logged-out visitor clicked any
 * "Start 14-Day Free Trial" CTA), this component navigates them to
 * `/pricing?start_trial=1` so the Pricing page's auto-resume effect can
 * fire the Stripe SetupIntent checkout automatically.
 *
 * Also handles the `founding_pending_tier` flag — when set, routes the user
 * back to `/founding?claim=1` after login so the Founding Member page's
 * auto-fire effect kicks in and triggers the Stripe subscription checkout
 * with the promo code pre-applied.
 *
 * This catches the Google OAuth path (which sends users to `/dashboard`
 * after callback) as well as any other future auth flow.
 *
 * Each intended page is responsible for clearing the corresponding flag
 * once the checkout has been triggered (so refresh doesn't loop).
 */
const TrialResumer = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    if (!user) return;

    let foundingIntent = null;
    try { foundingIntent = localStorage.getItem("founding_pending_tier"); } catch { /* ignore */ }
    if (foundingIntent) {
      if (location.pathname === "/founding") return; // let the page handle it
      handledRef.current = true;
      navigate("/founding?claim=1", { replace: true });
      return;
    }

    let intent = false;
    try { intent = localStorage.getItem("pending_trial_start") === "1"; } catch { /* ignore */ }
    if (!intent) return;
    // If we're already on /pricing, let that page's own auto-resume effect do its job.
    if (location.pathname === "/pricing") return;
    handledRef.current = true;
    navigate("/pricing?start_trial=1", { replace: true });
  }, [user, location.pathname, navigate]);

  return null;
};

export default TrialResumer;
